/**
 * Google Tag Manager + Google Analytics 4, behind consent.
 *
 * The rules, because each one is a DSGVO / § 25 TDDDG requirement and not a style:
 *
 *  - Nothing from Google is loaded until the visitor has said yes to "Statistik".
 *    Not the container, not gtag.js, not a preconnect. Loading GTM and then asking
 *    it to behave is not prior consent: the request alone sends the IP address.
 *  - Consent Mode v2 defaults to denied for everything; on consent only
 *    analytics_storage becomes granted. There are no ads, so the ad signals stay
 *    denied forever.
 *  - Events carry no personal data: no name, e-mail, user id, ticker, amount or
 *    portfolio figure. Page paths lose their query string and hash, which is where
 *    tokens live (e-mail confirmation links, for one).
 *  - Withdrawing consent deletes the _ga cookies and reloads, because a loaded
 *    container cannot be unloaded.
 *
 * The container and measurement ids come from the server (/api/site/cookie-config,
 * env GTM_CONTAINER_ID / GA_MEASUREMENT_ID), so analytics can be switched on and off
 * without a rebuild. With a GTM container the GA4 tag lives inside it; without one,
 * a bare GA4 id falls back to gtag.js. The GTM-side configuration this code expects
 * is written down in docs/ANALYTICS.md.
 */

let loaded = false;

function gtag() {
  // gtag() must push the `arguments` object itself, not an array — GTM tells the two
  // apart, and an array is read as a dataLayer event rather than a command.
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

function ensureDataLayer() {
  window.dataLayer = window.dataLayer || [];
}

/** Load the tags. Only ever called after analytics consent exists. */
export function loadAnalytics({ gtmId, gaId }) {
  if (loaded || (!gtmId && !gaId)) return;
  loaded = true;
  ensureDataLayer();
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
  });
  gtag("consent", "update", { analytics_storage: "granted" });

  const s = document.createElement("script");
  s.async = true;
  if (gtmId) {
    window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
    s.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`;
  } else {
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`;
    gtag("js", new Date());
    // Page views are sent by trackPageView on every route change, not by the config
    // call, or the first page would be counted twice.
    gtag("config", gaId, { send_page_view: false, allow_google_signals: false,
                           allow_ad_personalization_signals: false });
  }
  document.head.appendChild(s);
}

export const analyticsLoaded = () => loaded;

/** Push an event. A no-op unless the tags are loaded, i.e. unless consent exists. */
export function track(event, params = {}) {
  if (!loaded) return;
  window.dataLayer.push({ event, ...params });
}

// Routes whose path names a thing: a ticker, an investor, a post, a referral handle.
// Sent as the pattern, so Google learns which *kind* of page was opened and never
// which instrument or person someone looked at.
const ROUTE_PATTERNS = [
  [/^\/instrument\/[^/]+/, "/instrument/:ticker"],
  [/^\/investors\/[^/]+/, "/investors/:id"],
  [/^\/posts\/[^/]+/, "/posts/:slug"],
  [/^\/r\/[^/]+/, "/r/:handle"],
];

export function normalisePath(pathname) {
  for (const [re, pattern] of ROUTE_PATTERNS) {
    if (re.test(pathname)) return pattern;
  }
  return pathname;
}

/** One page view per SPA route change. The route pattern only — no ticker, id,
 *  query string or hash, and a fixed title, because titles carry the same names. */
export function trackPageView(pathname) {
  if (!loaded) return;
  const path = normalisePath(pathname);
  window.dataLayer.push({
    event: "page_view",
    page_path: path,
    page_location: window.location.origin + path,
    page_title: `Co Stock ${path}`,
  });
}

/** Withdrawal: drop the GA cookies on every domain level they may have been set on. */
export function clearAnalyticsCookies() {
  const host = window.location.hostname;
  const parts = host.split(".");
  const domains = ["", host];
  for (let i = 1; i < parts.length - 1; i++) domains.push("." + parts.slice(i).join("."));
  document.cookie.split(";").map((c) => c.split("=")[0].trim())
    .filter((name) => name === "_ga" || name.startsWith("_ga_") || name === "_gid" || name === "_gat")
    .forEach((name) => {
      domains.forEach((d) => {
        document.cookie = `${name}=; Max-Age=0; path=/${d ? `; domain=${d}` : ""}`;
      });
    });
}
